import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useMutation } from '@apollo/client';
import { Button } from 'semantic-ui-react';
import { connect } from 'react-redux';

import {
  IfScope,
  Notes,
  createEmptyState,
  createEditorStateFromNote,
  captureException,
  AUTHENTICATION_SCOPES,
} from '@playbookux/playbook-web-core';

import Transcripts from '../../../../core/components/video-tab/transcripts';
import ClipButton from '../../../../core/components/video-player/clip-button';
import MenuFlaps from '../../../../core/components/video-tab/menu-flaps';
import VideoPlayer, { withMedia } from '../../../../core/components/video-player';
import VideoPlayerWithOverlay from '../../../../core/components/video-player-with-video-overlay';
import { useUserScope } from '../../../../core/hooks';
import { MARK_TEST_SESSION_AS_VIEWED } from '../../../../core/graphql/test-sessions';
import { setError } from '../../../../core/actions';
import TagModule from '../../../components/tag-module';

import ClipModal from '../clip-modal';
import DeviceInfo from '../device-info';
import TesterInfo from '../tester-info';
import Modal from '../modal-rating';

import {
  ActiveMenu,
  ContainerMessage,
  Column,
  Container,
  VideoContainer,
  Grid,
  Message,
  RightPanel,
  MessageImage,
  CenterContent,
  ContainerBottom,
  RatingVideo,
  RatingContainer,
  ButtonContainer,
  NavButton,
} from './styles';

import { config } from '../../../../config';
import renderComponentToHTML from './marker-unmoderated';
import {
  deleteVerticalLine, getPositions, positionVerticalLine,
} from './marker-helper/marker-helper';
import { RECORDING_TYPE, PROCESSED_STATUS, TRANSCRIBE_ERROR_STATUS } from './constants';

const VideoFeatures = (props) => {
  const {
    tester,
    project,
    media,
    startTime,
    companyMembers,
    newSelectedNote,
    isEditClip,
    participants,
    testerLoading,
    participantsLoading,
    isAdmin,
    recordingsLoading,
    recordings,
  } = props;
  const {
    seekTo,
    pause,
    play,
    loadedData,
    currentTime,
  } = media;

  const [activeItem, setActiveItem] = useState(isEditClip ? 'Notes & Clips' : 'Transcripts');

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState();

  const [openRatingModal, setOpenRatingModal] = useState(false);
  const [intendedRating, setIntendedRating] = useState();

  const [editorState, setEditorState] = useState(createEmptyState());

  const [modalOpened, setModalOpened] = useState(false);
  const [videoError, setVideoError] = useState(null);

  const scope = useUserScope();
  const shareScope = scope === AUTHENTICATION_SCOPES.SHARE_PROJECT
    || scope === AUTHENTICATION_SCOPES.SHARE_CLIP
    || scope === AUTHENTICATION_SCOPES.SHARE_HIGHLIGHT_REEL;

  /**
   * Transcript autoscroll, do not use useState =) otherwise it wont work
   */
  // ref of component scroll bar
  const scrollBar = useRef();
  // ref of is autoscrolling that is scrolling or not
  const autoScrollTranscript = useRef(true);
  // ref stop scrolling when button show up
  const stopScrolling = useRef(false);

  const handleTabClick = (e, { name }) => {
    setActiveItem(name);
  };

  const handleClickOnCurrentTime = (time) => {
    seekTo(time);
  };

  const handleToggleOpenModal = () => {
    pause();
    setEditorState(createEmptyState());
    setModalOpen(!modalOpen);
    setSelectedNote(null);
  };

  const handleNoteSelected = useCallback((item, autoPlay = false) => {
    setEditorState(createEditorStateFromNote(item));
    seekTo(item.videoStartTime);
    if (autoPlay) play();

    setSelectedNote(item);
    setModalOpen(true);
  }, [play, seekTo, setModalOpen, setSelectedNote, setEditorState]);

  const handleCloseModal = () => {
    setEditorState(createEmptyState());
    setModalOpen(false);
    setSelectedNote(null);
  };

  const handleOpenRatingModal = (evt, { rating }) => {
    setIntendedRating(rating);
    setOpenRatingModal(true);
  };

  const handleCloseRatingModal = () => {
    setOpenRatingModal(false);
  };

  const handleTextChange = (value) => {
    setEditorState(value);
  };

  useEffect(() => {
    // We need to wait for video to initialize before we can call it.
    if (!_.isEmpty(newSelectedNote) && loadedData && !modalOpened) {
      handleNoteSelected(newSelectedNote, true);
      setModalOpened(true);
    }

    if (loadedData && startTime) {
      seekTo(startTime);
    }
  }, [loadedData, newSelectedNote, handleNoteSelected, modalOpened, startTime]);

  const { surveys } = project;

  const [markTestSessionAsViewed] = useMutation(MARK_TEST_SESSION_AS_VIEWED);

  const handleMarkTesterAsViewed = (testSessionId) => {
    if (shareScope) return;

    try {
      markTestSessionAsViewed({
        variables: {
          testSessionId,
        },
      });
    } catch (error) {
      captureException('MARK_TEST_SESSION_AS_VIEWED', error);
      setError(error.graphQLErrors[0].message);
    }
  };

  const goToVideo = (testerId) => {
    handleMarkTesterAsViewed(testerId);
    // eslint-disable-next-line no-restricted-globals
    window.open(`/projects/${project.id}/videos/${testerId}`, '_self');
  };

  const currentIndex = participants?.findIndex((p) => p.id === tester?.id);
  const prevIndexValue = currentIndex - 1;
  const nextIndexValue = currentIndex + 1;

  const handlePrev = () => {
    const prevIndex = (prevIndexValue + participants?.length) % participants?.length;
    const prevParticipantId = participants[prevIndex]?.id;
    goToVideo(prevParticipantId);
  };

  const handleNext = () => {
    const nextIndex = (nextIndexValue) % participants?.length;
    const nextParticipantId = participants[nextIndex]?.id;
    goToVideo(nextParticipantId);
  };

  const practiceTestProjectId = config.REACT_APP_PRACTICE_TEST_PROJECT_ID;

  const testSession = useMemo(() => {
    if (!tester || !tester.testSessions) {
      return {};
    }

    // when it is admin, we show the video, companies cant see the video if is removed
    if (isAdmin) {
      //  We need to validate if the project is a practice project to get the right test session
      //  Because it can have multiple sessions, we need to get the approved/submitted test session if it exists
      //  If it is not a practice project, we need to get the first test session
      if (project.id === practiceTestProjectId) {
        const sessionApproved = _.find(tester.testSessions, (session) => session.practiceTestStatus === 'approved');

        if (sessionApproved) return sessionApproved;

        const sessionSubmitted = _.find(tester.testSessions, (session) => session.practiceTestStatus === 'submitted');

        if (sessionSubmitted) return sessionSubmitted;
      }

      return tester.testSessions[0] || {};
    }

    if (project.testType === 'upload') {
      return tester.testSessions
        .filter((ts) => (ts.status === PROCESSED_STATUS || ts.status === TRANSCRIBE_ERROR_STATUS) && ts.removed === false)[0] || {};
    }

    return tester.testSessions
      .filter((ts) => ts.status === PROCESSED_STATUS && ts.removed === false)[0] || {};
  }, [tester]);

  const showTestSession = useMemo(() => Object.keys(testSession).length > 0, [testSession]);

  const { testSessionAnswers: answers, videoUrl } = testSession || {};

  if (!answers && !testerLoading && project.testType === 'unmoderated') {
    return <Message>{'This unmoderated video doesn\'t have any answer.'}</Message>;
  }

  const {
    id: testSessionId,
    ratingDescription,
    ratingPrivate,
    rating,
  } = testSession;

  const lastIndex = participants.length - 1;
  const isLastIndex = currentIndex === lastIndex;
  const isFirstIndex = currentIndex === 0;
  const isFaceRecording = project.faceRecording && recordings.some((recording) => recording.type === RECORDING_TYPE.FACE_RECORDING);
  const screenAndVoiceVideo = recordings.find((recording) => recording.type === RECORDING_TYPE.SCREEN_AND_VOICE)?.videoUrl || videoUrl;
  const faceRecordingVideo = recordings.find((recording) => recording.type === RECORDING_TYPE.FACE_RECORDING)?.videoUrl;
  const finalRecordingDownloadLink = recordings.find((recording) => recording.type === RECORDING_TYPE.FINAL_RECORDING)?.videoUrl;
  const transcripts = _.isEmpty(testSession?.testSessionAnswers) ? testSession?.testSessionSpeakers : testSession?.testSessionAnswers;
  const transcriptHighlights = transcripts?.flatMap((transcript) => transcript.transcriptHighlights);

  // this is to highlight text
  const buildTranscriptMarkers = transcriptHighlights?.map((answer) => ({
    time: answer?.timeStart,
    timeEnd: answer?.timeEnd,
    html: renderComponentToHTML({
      questionType: null,
      videoStartTime: null,
      label: null,
      isMarker: false,
    }),
    isMarker: false,
    text: answer?.text,
  }));

  const isModerated = project.testType === 'moderated';
  const buildMarkers = transcripts
    ?.map((answer) => ({
      time: answer.videoStartTime,
      html: renderComponentToHTML({
        questionType: answer.question?.questionType,
        videoStartTime: answer.videoStartTime,
        label: answer.question?.label,
        isMarker: true,
      }),
      isMarker: true,
    }));
  const mergedMarkers = _.unionWith(buildTranscriptMarkers, buildMarkers, _.isEqual);

  const markers = {
    markerStyle: {
      width: '10px',
      height: '10px',
      'background-color': 'white',
      'border-radius': '50%',
      cursor: 'pointer',
      position: 'absolute',
      top: '-12px',
      display: isModerated ? 'none' : 'block',
    },
    markers: mergedMarkers,
    markerTip: {
      html(marker) {
        return marker.html;
      },
    },
    onMarkerReached: (marker) => {
      let positions = null;
      let parentElement = null;
      const result = getPositions(marker);
      if (result) {
        ({ positions, parentElement } = result);
      }
      deleteVerticalLine();
      // when you click the progress bar, this is also is called
      if (autoScrollTranscript.current && scrollBar.current && marker.isMarker) {
        const targetElement = document.querySelectorAll(`[data-transcript-video-start-time="${marker.time}"]`)[0];

        if (targetElement && scrollBar.current) {
          const { offsetTop } = targetElement;
          stopScrolling.current = true;
          // when initializing if the time is 0, we need to scroll to the top to show the toggle button
          if (marker.time !== 0) {
            scrollBar.current.scrollTop(offsetTop);
          } else {
            scrollBar.current.scrollTop(0);
          }
        }
      }
      // this is a gambiarra for performance, but it works so well.
      if (!marker.isMarker) {
        positionVerticalLine(positions, parentElement);
      }
    },
  };

  return (
    <>
      <Container>
        <Grid columns={2} stackable>
          <Column width={11}>
            <>
              <VideoContainer>
                {videoError && (
                  <ContainerMessage>
                    <CenterContent>
                      <MessageImage src="/images/cross-icon.png" />
                      <Message>
                        This video is corrupted or incompatible.
                      </Message>
                    </CenterContent>
                  </ContainerMessage>
                )}
                {(testerLoading || Object.keys(testSession).length === 0) && (
                  <ContainerMessage>
                    <CenterContent>
                      <MessageImage src="/images/clock.png" />
                      <Message>
                        Your video is processing
                      </Message>
                    </CenterContent>
                  </ContainerMessage>
                )}
                {!testerLoading && !recordingsLoading && showTestSession && !videoError && (
                  <>
                    {isFaceRecording ? (
                      <VideoPlayerWithOverlay
                        screenRecordingSrc={screenAndVoiceVideo}
                        faceRecordingSrc={faceRecordingVideo}
                        downloadLink={finalRecordingDownloadLink}
                        markers={markers}
                        onError={(err) => setVideoError(err)}
                      >
                        <ClipButton
                          onClick={handleToggleOpenModal}
                          label={modalOpen ? 'Cancel Clip' : 'Create Clip'}
                        />

                        <ClipModal
                          isOpen={modalOpen}
                          note={selectedNote}
                          onClose={handleCloseModal}
                          testSessionId={testSessionId}
                          companyId={project.company.id}
                          editorState={editorState}
                          onChange={handleTextChange}
                          companyMembers={companyMembers}
                        />
                      </VideoPlayerWithOverlay>
                    ) : (
                      <VideoPlayer
                        src={screenAndVoiceVideo}
                        downloadLink={finalRecordingDownloadLink}
                        markers={markers}
                        onError={(err) => setVideoError(err)}
                      >
                        <ClipButton
                          onClick={handleToggleOpenModal}
                          label={modalOpen ? 'Cancel Clip' : 'Create Clip'}
                        />

                        <ClipModal
                          isOpen={modalOpen}
                          note={selectedNote}
                          onClose={handleCloseModal}
                          testSessionId={testSessionId}
                          companyId={project.company.id}
                          editorState={editorState}
                          onChange={handleTextChange}
                          companyMembers={companyMembers}
                        />
                      </VideoPlayer>
                    )}
                  </>
                )}
              </VideoContainer>

              <ContainerBottom>
                <RatingContainer>
                  <TesterInfo loading={testerLoading} tester={tester} />
                  <RatingVideo
                    icon="star"
                    maxRating={5}
                    rating={rating}
                    onRate={handleOpenRatingModal}
                  />
                </RatingContainer>
                <ButtonContainer>
                  <Button.Group>
                    <NavButton
                      basic
                      color={isFirstIndex ? 'grey' : 'black'}
                      labelPosition="left"
                      icon="long arrow alternate left"
                      content="Previous"
                      onClick={handlePrev}
                      disabled={isFirstIndex || participantsLoading}
                      loading={participantsLoading}
                    />
                    <NavButton
                      basic
                      color={isLastIndex ? 'grey' : 'black'}
                      labelPosition="right"
                      icon="long arrow alternate right"
                      content="Next"
                      onClick={handleNext}
                      disabled={isLastIndex || participantsLoading}
                      loading={participantsLoading}
                    />
                  </Button.Group>
                </ButtonContainer>
              </ContainerBottom>

              <DeviceInfo loading={testerLoading} testSession={testSession} />

              {openRatingModal && (
                <Modal
                  label={`You've rated ${_.get(tester, 'user.profile.firstName', '')}`}
                  onClose={handleCloseRatingModal}
                  id={testSessionId}
                  ratingDescription={ratingDescription}
                  ratingPrivate={ratingPrivate}
                  rating={intendedRating}
                />
              )}
            </>
          </Column>

          <Column width={5}>
            <RightPanel>
              <MenuFlaps
                activeItem={activeItem}
                onClickMenuItem={handleTabClick}
              />

              {activeItem === 'Transcripts' && showTestSession && (
                <ActiveMenu activeItem={activeItem} item="Transcripts">
                  <Transcripts
                    testType={project.testType}
                    onCurrentTime={handleClickOnCurrentTime}
                    testSessionId={testSessionId}
                    testSession={testSession}
                    loading={testerLoading}
                    isDemoTestSession={project.id === '00000000-0000-0000-0000-000000000000'}
                    surveys={surveys}
                    scrollBar={scrollBar}
                    autoScrollTranscript={autoScrollTranscript}
                    stopScrolling={stopScrolling}
                    currentTime={currentTime}
                  />
                </ActiveMenu>
              )}

              {activeItem === 'Notes & Clips' && showTestSession && (
                <ActiveMenu activeItem={activeItem} item="Notes & Clips">
                  <IfScope user shareProject scope={scope}>
                    <Notes
                      project={project}
                      readOnly={shareScope}
                      onCurrentTime={handleClickOnCurrentTime}
                      onNoteSelected={handleNoteSelected}
                      testSessionId={testSessionId}
                      defaultUrl={config.REACT_APP_PLATFORM_URI}
                    />
                  </IfScope>
                </ActiveMenu>
              )}

              <IfScope user scope={scope}>
                <TagModule testSessionId={testSessionId} />
              </IfScope>
            </RightPanel>
          </Column>
        </Grid>
      </Container>
    </>
  );
};

VideoFeatures.defaultProps = {
  tester: {},
  startTime: 0,
};

VideoFeatures.propTypes = {
  tester: PropTypes.shape({
    id: PropTypes.string.isRequired,
    testSessions: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      videoUrl: PropTypes.string.isRequired,
      testSessionAnswers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      ratingDescription: PropTypes.string,
      ratingPrivate: PropTypes.bool,
      rating: PropTypes.number,
      filter: PropTypes.func,
    })),
  }),
  isEditClip: PropTypes.bool,
  participants: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })),
  project: PropTypes.shape({
    id: PropTypes.string.isRequired,
    testType: PropTypes.string.isRequired,
    surveys: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    company: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    faceRecording: PropTypes.bool.isRequired,
  }).isRequired,
  companyMembers: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  startTime: PropTypes.number,
  newSelectedNote: PropTypes.shape({
    id: PropTypes.string,
  }),
  media: PropTypes.shape({
    currentTime: PropTypes.number,
    loadedData: PropTypes.bool,
    seekTo: PropTypes.func,
    pause: PropTypes.func,
    play: PropTypes.func,
  }).isRequired,
  testerLoading: PropTypes.bool,
  participantsLoading: PropTypes.bool,
  isAdmin: PropTypes.bool,
  recordingsLoading: PropTypes.bool,
  recordings: PropTypes.arrayOf(PropTypes.shape()),
};

VideoFeatures.defaultProps = {
  isEditClip: false,
  newSelectedNote: undefined,
  testerLoading: true,
  participantsLoading: true,
  recordingsLoading: true,
  participants: [],
  isAdmin: false,
  recordings: [],
};

const mapStateToProps = ({ authentication }) => ({
  isAdmin: authentication?.user?.admin || false,
});

export default withMedia(connect(mapStateToProps, null)(VideoFeatures));
